import { useMsal } from '@azure/msal-react';
import { makeStyles, mergeClasses, Button, Option, Tab, TabList, Text, Tooltip } from '@fluentui/react-components';
import { mapAsync } from 'es-toolkit/array';
import parse from 'html-react-parser';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { downloadFile } from '../../common/file/download-file';
import { useCommonStyles, useStores } from '../../common/hooks';
import { isAiEvalTabEnabled } from '../../configurations';
import { getCleanHtmlString } from '../../common/html-string/get-clean-html-string';
import { TooltipFieldName } from '../../common/tooltip/tooltip-field-name';
import { getTooltipContentByFieldName } from '../../common/tooltip/tooltip-get-content';
import {
  AwardJudge,
  blankProgramCountryLead,
  blankProgramCountryNominationReviewer,
  COUNTRY_AWARD_MAX_WINNERS,
  NominationRole, ProgramCountryLead, ProgramCountryNominationReviewer
} from '../../models';
import { ResourceTypes } from '../../models/resource-services';
import { CountryRouteParams } from '../../models/route-params';
import { PageTitle } from '../shared-components';
import { AssignWinnerSection } from '../shared-components/assign-winner-section/AssignWinnerSection';
import { FlexBox } from '../shared-components/flex-box';
import { LeadAssignment } from '../shared-components/lead-assignment/LeadAssignment';
import Loading from '../shared-components/Loading';
import { CountryAwardStatisticSection } from './components/CountryAwardStatisticSection';
import { CountryNominationSummaryTable } from './components/CountryNominationSummaryTable';
import { EvaluationComparisonPanel } from '../page-award/components/EvaluationComparisonPanel';
import { CountryAwardTabTypeCode } from './country-award-page-ui-store';
import { SelectOptions } from '../shared-components/form';
import { JudgeAssignmentModal } from '../shared-components/JudgeAssignmentModal';
import './CountryAwardPage.scss';

const useStyles = makeStyles({
  root: {
    alignItems: 'center',
  },
});

const tabDisplayNames: Record<CountryAwardTabTypeCode, string> = {
  [CountryAwardTabTypeCode.Nominations]: 'Nominations',
  [CountryAwardTabTypeCode.Details]: 'Details',
  [CountryAwardTabTypeCode.AiEval]: 'AI Eval',
};

const tabTooltipFieldNames: Partial<Record<CountryAwardTabTypeCode, TooltipFieldName>> = {
  [CountryAwardTabTypeCode.Nominations]: 'nominationTab',
  [CountryAwardTabTypeCode.Details]: 'detailsTab',
};

const CountryAwardPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    userStore,
    countryStore,
    awardStore,
    nominationForStaffPageUiStore,
    countryAwardPageUiStore,
  } = useStores();

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { countryId: countryIdParam } = useParams<CountryRouteParams>();
  const countryId = Number(countryIdParam);
  const isCountryIdValid = countryIdParam !== undefined && Number.isInteger(countryId) && countryId > 0;

  const handleAssignProgramCountryLead = async (emailAddress: string, isPrimary: boolean): Promise<void> => {
    if (!awardStore.defaultProgramRef || !isCountryIdValid) return;

    setIsLoading(true);
    const newProgramCountryLead = blankProgramCountryLead();
    newProgramCountryLead.programId = awardStore.defaultProgramRef.programId;
    newProgramCountryLead.countryId = countryId;
    newProgramCountryLead.emailAddress = emailAddress;
    newProgramCountryLead.isPrimary = isPrimary;
    try {
      await apiStore.assignProgramCountryLead(instance, awardStore.defaultProgramRef.programId, countryId, newProgramCountryLead);
      await getProgramCountryLeads(countryId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteProgramCountryLead = async (programCountryLead: ProgramCountryLead): Promise<void> => {
    setIsLoading(true);
    try {
      await apiStore.deleteProgramCountryLead(instance, programCountryLead.programCountryLeadId);
      await getProgramCountryLeads(countryId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteProgramCountryNominationReviewer = async (
    programCountryNominationReviewer: ProgramCountryNominationReviewer,
  ): Promise<void> => {
    try {
      setIsLoading(true);
      await apiStore.deleteProgramCountryNominationReviewer(
        instance,
        programCountryNominationReviewer.programCountryNominationReviewerId,
      );
      await getProgramCountryNominationReviewers(countryId);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleAssignProgramCountryNominationReviewer = async (emailAddress: string): Promise<void> => {
    if (!awardStore.defaultProgramRef || !isCountryIdValid) return;

    setIsLoading(true);
    const newProgramCountryNominationReviewer = blankProgramCountryNominationReviewer();
    newProgramCountryNominationReviewer.programId = awardStore.defaultProgramRef.programId;
    newProgramCountryNominationReviewer.countryId = countryId;
    newProgramCountryNominationReviewer.emailAddress = emailAddress;
    try {
      await apiStore.assignProgramCountryNominationReviewer(
        instance,
        awardStore.defaultProgramRef.programId,
        countryId,
        newProgramCountryNominationReviewer,
      );
      await getProgramCountryNominationReviewers(countryId);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleDismissJudgeAssignmentModal = async (): Promise<void> => {
    try {
      setIsLoading(true);
      countryAwardPageUiStore.setShowJudgeAssignmentModal(false);
      countryAwardPageUiStore.setJudgesToAdd([]);

      if (countryAwardPageUiStore.countryAward) {
        await getCountryAwardJudges(countryAwardPageUiStore.countryAward.awardId);
      }
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const assignAwardJudges = async (): Promise<void> => {
    try {
      const awardId = countryAwardPageUiStore.countryAward?.awardId;
      if (!awardId) return;

      const assignedJudges = await mapAsync(
        countryAwardPageUiStore.judgesToAdd,
        async (judge) => {
          const judgeRef = await apiStore.assignCountryAwardJudge(instance, awardId, countryId, judge);
          return {
            ...judge,
            awardJudgeId: judgeRef.awardJudgeId,
          };
        },
      );
      countryAwardPageUiStore.addAwardJudges(assignedJudges);
    } catch (error) {
      // TODO - handle error
    }
  }

  const handleSubmitJudgeAssignmentModal = async (): Promise<void> => {
    try {
      countryAwardPageUiStore.setShowJudgeAssignmentModal(false);
      setIsLoading(true);

      await assignAwardJudges();

      countryAwardPageUiStore.setJudgesToAdd([]);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeleteJudge = async (judge: AwardJudge): Promise<void> => {
    if (judge.awardJudgeId === 0) {
      countryAwardPageUiStore.removeAwardJudge(judge);
      return;
    }

    try {
      setIsLoading(true);
      await apiStore.deleteCountryAwardJudge(instance, judge.awardJudgeId, countryId);
      const awardId = countryAwardPageUiStore.countryAward?.awardId;
      if (awardId) await getCountryAwardJudges(awardId);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleUpdateJudgeStatus = async (judge: AwardJudge, status: boolean): Promise<void> => {
    if (judge.awardJudgeId === 0) {
      countryAwardPageUiStore.setAwardJudgeStatus(judge, status);
      return;
    }

    try {
      setIsLoading(true);
      await apiStore.updateCountryAwardJudgeStatus(instance, judge.awardJudgeId, countryId, status);
      const awardId = countryAwardPageUiStore.countryAward?.awardId;
      if (awardId) await getCountryAwardJudges(awardId);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleOpenJudgeAssignmentModal = async (): Promise<void> => {
    if (!countryAwardPageUiStore.countryAward) return;

    try {
      setIsLoading(true);
      await getCountryAwardJudges(countryAwardPageUiStore.countryAward.awardId);
      countryAwardPageUiStore.setShowJudgeAssignmentModal(true);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const handleUpdateAwardNominationRole = async (nominationRole: NominationRole): Promise<void> => {
    try {
      setIsLoading(true);
      await updateProgramCountryNominationRole(nominationRole);
      await getCountryAwardNominationSummaries(countryId);
      countryAwardPageUiStore.setSelectedCountryNominationSummary(undefined);
      countryAwardPageUiStore.setSelectedNominationRole(undefined);
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const updateCountryAwardConfiguration = async (newValue: number): Promise<void> => {
    if (!countryAwardPageUiStore.countryAward) return;
    try {
      setIsLoading(true);
      await apiStore.updateAwardConfiguration(instance, countryAwardPageUiStore.countryAward.awardId, {
        judgeCapacity: newValue,
      });
      await getProgramCountryAward()
    } catch (error) {
      // TODO - handle error
    } finally {
      setIsLoading(false);
    }
  }

  const getCountryAwardJudgeCapacityOptions = async (): Promise<void> => {
    try {
      if (!countryAwardPageUiStore.countryAward) return;
      const judgeCapacityOptions = await apiStore.getAwardJudgeCapacityOptions(instance, countryAwardPageUiStore.countryAward.awardId);
      countryAwardPageUiStore.setJudgeCapacityOptions(judgeCapacityOptions);
    } catch (error) {
      // TODO - handle error
    }
  };

  const downloadProgramCountryExportedNominationsSummaries = async (
    countryAwardId: number,
    programId: number,
    countryId: number,
    selectedFields: string[],
    searchText: string,
  ): Promise<void> => {
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, {
        data: {
          routeData: {
            awardId: countryAwardId,
            programId: programId,
            countryId: countryId,
          },
          queryString: {
            selectedFields: selectedFields.join(','),
            searchText: searchText ? searchText : undefined,
          }
        },
        resourceType: ResourceTypes.ExportedProgramCountryAwardNominationSummaries,
      });
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getCountryAwardJudges = async (awardId: number): Promise<void> => {
    try {
      const awardJudges = await apiStore.getCountryAwardJudges(instance, awardId, countryId);
      countryAwardPageUiStore.setAwardJudges(awardJudges);
    } catch (error) {
      // TODO - handle error
    }
  };

  const isDisableCountryLeadAssignment = (): boolean => {
    return !(userStore.isAdmin || userStore.isRegionLead);
  }

  const isDisableCountryNominationReviewerAssignment = (): boolean => {
    return !(userStore.isAdmin || userStore.isRegionLead || userStore.isCountryLead);
  };

  const canAssignWinners = (): boolean => {
    return (
      (awardStore.isReviewAndDesignateWindowOpen && (
        userStore.isAdmin ||
        userStore.isRegionLead ||
        userStore.isCountryLead
      )) ||
      (awardStore.isComplianceAndRevisionsWindowOpen && userStore.isAdmin)
    );
  };

  const canTriggerAiScrub = (): boolean =>
    userStore.isAdmin || userStore.isRegionLead || userStore.isCountryLead || userStore.isCountryNominationReviewer;

  const getProgramCountryAward = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const countryAward = await apiStore.getProgramCountryAward(instance, awardStore.defaultProgramRef.programId);
      countryAwardPageUiStore.setCountryAward(countryAward);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const getProgramDatesForStaff = async (): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const programDates = await apiStore.getProgramDatesForStaff(instance, awardStore.defaultProgramRef.programId);
      countryAwardPageUiStore.setStaffProgramDates(programDates);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getCountryAwardNominationSummaries = async (countryId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const nominationRefs = await apiStore.getCountryAwardNominationSummaries(instance, awardStore.defaultProgramRef.programId, countryId);
      countryAwardPageUiStore.setCountryNominationSummaries(nominationRefs);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const getCountryAwardStatistic = async (countryId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const countryAwardStatistic = await apiStore.getCountryAwardStatistic(instance, awardStore.defaultProgramRef.programId, countryId);
      countryAwardPageUiStore.setCountryAwardStatistic(countryAwardStatistic);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getProgramCountryLeads = async (countryId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const programCountryLeads = await apiStore.getProgramCountryLeads(instance, awardStore.defaultProgramRef.programId, countryId);
      countryAwardPageUiStore.setProgramCountryLeads(programCountryLeads);
    } catch (error) {
      // TODO - handle error
    }
  };

  const getProgramCountryNominationReviewers = async (countryId: number): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      const programCountryNominationReviewers = await apiStore.getProgramCountryNominationReviewers(instance, awardStore.defaultProgramRef.programId, countryId);
      countryAwardPageUiStore.setProgramCountryNominationReviewers(programCountryNominationReviewers);
    } catch (error) {
      // TODO - handle error
    }
  };

  const updateProgramCountryNominationRole = async (nominationRole: NominationRole): Promise<void> => {
    if (!awardStore.defaultProgramRef) return;
    try {
      await apiStore.updateProgramCountryNominationRole(instance, awardStore.defaultProgramRef.programId, countryId, nominationRole.nominationId, nominationRole);
    } catch (error) {
      // TODO - handle error
    }
  };

  const preloadData = async (): Promise<void> => {
    setIsLoading(true);

    if (!countryId) {
      setIsLoading(false);
      return;
    }

    await Promise.allSettled([
      getProgramCountryAward(),
      getProgramDatesForStaff(),
      getCountryAwardNominationSummaries(countryId),
      getCountryAwardStatistic(countryId),
      getProgramCountryLeads(countryId),
      getProgramCountryNominationReviewers(countryId),
    ]);

    if (countryAwardPageUiStore.countryAward) {
      await getCountryAwardJudges(countryAwardPageUiStore.countryAward.awardId);
    }

    await getCountryAwardJudgeCapacityOptions();

    setIsLoading(false);
  }

  useEffect(() => {
    preloadData();
    return () => {
      countryAwardPageUiStore.resetStates();
    }
  }, [countryId]);

  const showAiEvalTab = isAiEvalTabEnabled
    && (userStore.isAdmin || userStore.isRegionLead || userStore.isCountryLead || userStore.isCountryNominationReviewer);

  const visibleCountryAwardTabTypeCodes = showAiEvalTab
    ? [...countryAwardPageUiStore.activeCountryAwardTabTypeCodes, CountryAwardTabTypeCode.AiEval]
    : countryAwardPageUiStore.activeCountryAwardTabTypeCodes;

  const renderCountryAwardTab = (tabTypeCode: CountryAwardTabTypeCode, index: number): React.JSX.Element => {
    const tooltipFieldName = tabTooltipFieldNames[tabTypeCode];
    const tab = <Tab value={tabTypeCode}>{tabDisplayNames[tabTypeCode]}</Tab>;
    return (
      <React.Fragment key={tabTypeCode}>
        {
          index > 0 &&
          <Text weight='bold'>|</Text>
        }
        {
          tooltipFieldName
            ? (
              <Tooltip
                withArrow
                relationship='description'
                content={{
                  children: getTooltipContentByFieldName('staff-award-page', tooltipFieldName) ?? '',
                  className: commonStyles.tooltip,
                }}
              >
                {tab}
              </Tooltip>
            )
            : tab
        }
      </React.Fragment>
    );
  };

  return (
    <FlexBox
      direction='column'
      content='start'
      className='country-award-page'
    >
      <JudgeAssignmentModal
        isModalOpen={countryAwardPageUiStore.isShowJudgeAssignmentModal}
        activeJudgesCount={countryAwardPageUiStore.activeJudgesCount}
        inactiveJudgesCount={countryAwardPageUiStore.inactiveJudgesCount}
        addedJudges={countryAwardPageUiStore.judgesToAdd}
        judges={countryAwardPageUiStore.awardJudges}
        onAddJudges={(emails) => countryAwardPageUiStore.onAddJudges(emails)}
        onSubmitModal={handleSubmitJudgeAssignmentModal}
        onDismissModal={handleDismissJudgeAssignmentModal}
        onDeleteJudge={handleDeleteJudge}
        onUpdateJudgeStatus={handleUpdateJudgeStatus}
      />
      {
        isLoading && (
          <Loading
            isFullScreen
          />
        )
      }
      {
        isCountryIdValid && countryAwardPageUiStore.countryAward && (
          <>
            <PageTitle
              title={`${countryStore.awardCountry(countryId)?.displayName} - Country Award`}
              subtitle={awardStore.defaultProgramRef?.displayName}
            />
            <TabList
              size='large'
              className={mergeClasses('country-award-page__tab-list', styles.root)}
              selectedValue={countryAwardPageUiStore.selectedCountryAwardTabTypeCode}
              onTabSelect={(_, data) =>
                countryAwardPageUiStore.setSelectedCountryAwardTabTypeCode(data.value as CountryAwardTabTypeCode)
              }
            >
              {
                visibleCountryAwardTabTypeCodes.map((tabTypeCode, index) => renderCountryAwardTab(tabTypeCode, index))
              }
            </TabList>
            {
              countryAwardPageUiStore.selectedCountryAwardTabTypeCode === CountryAwardTabTypeCode.Nominations && awardStore.defaultProgramRef && (
                <FlexBox
                  isFullWidth
                  className='country-award-page__main country-award-page__main--nominations'
                >
                  <CountryNominationSummaryTable
                    canAssignWinners={canAssignWinners()}
                    canTriggerAiScrub={canTriggerAiScrub()}
                    defaultScrubbingRubric={countryAwardPageUiStore.countryAward.defaultScrubbingRubric}
                    countryAwardId={countryAwardPageUiStore.countryAward.awardId}
                    countryId={countryId}
                    programId={awardStore.defaultProgramRef.programId}
                    countryNominationSummaries={countryAwardPageUiStore.countryNominationSummaries}
                    isJudgingWindowOpen={awardStore.isJudgingWindowOpen}
                    isScrubbingWindowOpen={awardStore.isScrubbingWindowOpen}
                    downloadProgramCountryExportedNominationsSummaries={downloadProgramCountryExportedNominationsSummaries}
                    onSetSortedCountryNominationSummaries={(summaries) => nominationForStaffPageUiStore.setSortedCountryNominationSummaries(summaries)}
                    onBatchTerminal={async () => {
                      await getCountryAwardNominationSummaries(countryId);
                      await getCountryAwardStatistic(countryId);
                    }}
                  />
                  {
                    canAssignWinners() && (
                      <AssignWinnerSection
                        isWinnerAssigned={countryAwardPageUiStore.isWinnerAssigned}
                        maxWinners={COUNTRY_AWARD_MAX_WINNERS}
                        selectedNominationSummary={countryAwardPageUiStore.selectedCountryNominationSummary}
                        selectedNominationRole={countryAwardPageUiStore.selectedNominationRole}
                        isCountryAward
                        handleUpdateAwardNominationRole={(nominationRole) => handleUpdateAwardNominationRole(nominationRole)}
                      />
                    )
                  }
                  {
                    !canAssignWinners() && !awardStore.isWinnersWindowOpen && (
                      <CountryAwardStatisticSection />
                    )
                  }
                </FlexBox>
              )
            }
            {
              countryAwardPageUiStore.selectedCountryAwardTabTypeCode === CountryAwardTabTypeCode.Details && (
                <FlexBox
                  className='country-award-page__main country-award-page__main--details'
                >
                  <div className={mergeClasses(commonStyles.richTextContent, 'details__summary')}>
                    {parse(getCleanHtmlString(countryAwardPageUiStore.countryAward.summary))}
                  </div>
                  <FlexBox
                    direction='column'
                    className='details__assignments'
                  >
                    <LeadAssignment
                      leads={countryAwardPageUiStore.programCountryLeads}
                      isPrimaryLead
                      title='Primary award lead'
                      allowMultipleEmailAddresses={false}
                      onAddLead={emailAddress => handleAssignProgramCountryLead(emailAddress, true)}
                      onDeleteLead={lead => handleDeleteProgramCountryLead(lead as ProgramCountryLead)}
                      disabled={isDisableCountryLeadAssignment()}
                      titleTooltipText={getTooltipContentByFieldName('staff-award-page', 'primaryAwardLeadTitle')}
                    />
                    <LeadAssignment
                      leads={countryAwardPageUiStore.programCountryLeads}
                      isPrimaryLead={false}
                      title='Award assistants'
                      allowMultipleEmailAddresses={false}
                      onAddLead={emailAddress => handleAssignProgramCountryLead(emailAddress, false)}
                      onDeleteLead={lead => handleDeleteProgramCountryLead(lead as ProgramCountryLead)}
                      disabled={isDisableCountryLeadAssignment()}
                      titleTooltipText={getTooltipContentByFieldName('staff-award-page', 'awardAssistantsTitle')}
                    />
                    <LeadAssignment
                      leads={countryAwardPageUiStore.programCountryNominationReviewers}
                      isPrimaryLead={false}
                      title='Nomination Review Team Members'
                      allowMultipleEmailAddresses={false}
                      onAddLead={handleAssignProgramCountryNominationReviewer}
                      onDeleteLead={nominationReviewer => handleDeleteProgramCountryNominationReviewer(nominationReviewer as ProgramCountryNominationReviewer)}
                      disabled={isDisableCountryNominationReviewerAssignment()}
                    />

                    {
                      (
                        userStore.isAdmin ||
                        userStore.isGroupLead ||
                        userStore.isAwardLead
                      ) && (
                        <SelectOptions
                          label='Judge Capacity'
                          tooltipText='Select the maximum number of nominations 1 judge can evaluate.'
                          onOptionSelect={(data) => {
                            if (!data.optionValue) return;
                            updateCountryAwardConfiguration(Number(data.optionValue));
                          }}
                          dropdownProps={{
                            value: countryAwardPageUiStore.selectedJudgeCapacityValue,
                            selectedOptions: countryAwardPageUiStore.selectedJudgeCapacityOptions,
                            disabled: !countryAwardPageUiStore.judgeCapacityOptions.length,
                          }}
                        >
                          {
                            countryAwardPageUiStore.judgeCapacityOptions.map((option) => (
                              <Option
                                key={option.value}
                                value={String(option.value)}
                              >
                                {option.text}
                              </Option>
                            ))
                          }
                        </SelectOptions>
                      )
                    }

                    <FlexBox
                      aligns='start'
                      content='space-between'
                    >
                      <FlexBox>
                        <Text weight='semibold'>
                          {'Judges ('}
                        </Text>
                        <Tooltip
                          withArrow
                          relationship='description'
                          content={{
                            children: getTooltipContentByFieldName('staff-award-page', 'activeJudges') ?? '',
                            className: commonStyles.tooltip,
                          }}
                        >
                          <Text
                            className='text--green'
                          >
                            {`${countryAwardPageUiStore.activeJudgesCount} active`}
                          </Text>
                        </Tooltip>
                        <Tooltip
                          withArrow
                          relationship='description'
                          content={{
                            children: getTooltipContentByFieldName('staff-award-page', 'inactiveJudges') ?? '',
                            className: commonStyles.tooltip,
                          }}
                        >
                          <Text>{` / ${countryAwardPageUiStore.inactiveJudgesCount} inactive)`}</Text>
                        </Tooltip>
                      </FlexBox>
                      <Button
                        appearance='primary'
                        onClick={handleOpenJudgeAssignmentModal}
                        disabled={!(
                          userStore.isAdmin ||
                          userStore.isGroupLead ||
                          userStore.isAwardLead ||
                          userStore.isCountryLead
                        )}
                      >
                        View / Edit
                      </Button>
                    </FlexBox>
                  </FlexBox>
                </FlexBox>
              )
            }
            {
              countryAwardPageUiStore.selectedCountryAwardTabTypeCode === CountryAwardTabTypeCode.AiEval && showAiEvalTab && (
                <FlexBox
                  isFullWidth
                  className='country-award-page__main'
                >
                  <EvaluationComparisonPanel awardId={countryAwardPageUiStore.countryAward.awardId} scope='country-awards' countryId={countryId} />
                </FlexBox>
              )
            }
          </>
        )
      }
      {
        !isCountryIdValid && (
          <Text
            block
            align='center'
            as='p'
          >
            Sorry, no country is currently selected.
          </Text>
        )
      }
    </FlexBox>
  );
}

export const CountryAwardPage = observer(CountryAwardPageImpl);
